"use client";

import { useMemo } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Heading,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

const MetaReviewCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useCustomTheme();

  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");

  const storedState = useMemo(() => {
    return sessionStorage.getItem("metaReviewOAuthState");
  }, []);

  const hasCode = Boolean(code);
  const hasState = Boolean(state);
  const stateMatches = Boolean(state && storedState && state === storedState);
  const hasMetaError = Boolean(error || errorDescription);

  const getStatus = () => {
    if (hasMetaError) {
      return {
        status: "error" as const,
        title: "Meta permission grant failed",
        description:
          errorDescription ||
          error ||
          "Meta returned an error during the permission grant flow.",
      };
    }

    if (!hasCode) {
      return {
        status: "warning" as const,
        title: "Authorization code missing",
        description:
          "Meta returned to PickDengu, but no authorization code was found. This is expected only when opening this page directly.",
      };
    }

    if (!hasState) {
      return {
        status: "warning" as const,
        title: "Security state missing",
        description:
          "Meta returned an authorization code, but the state value was missing.",
      };
    }

    if (storedState && !stateMatches) {
      return {
        status: "error" as const,
        title: "Security state mismatch",
        description:
          "The returned state does not match the state created before Meta Login started.",
      };
    }

    return {
      status: "success" as const,
      title: "Meta permission grant completed",
      description:
        "The reviewer has returned from Meta Login successfully. Continue to the Meta Review Login page, then open the review dashboard.",
    };
  };

  const status = getStatus();

  return (
    <Box
      minH="100vh"
      bg={theme.background}
      color={theme.text}
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={{ base: 4, md: 6 }}
      py={8}
    >
      <Container maxW="720px" p={0}>
        <Box
          p={{ base: 6, md: 8 }}
          borderWidth="1px"
          borderRadius="24px"
          bg="rgba(255,255,255,0.96)"
          borderColor="rgba(15,23,42,0.08)"
          boxShadow="0 18px 55px rgba(0,0,0,0.14)"
        >
          <VStack align="stretch" spacing={6}>
            <Stack spacing={2}>
              <Badge
                alignSelf="flex-start"
                colorScheme="blue"
                borderRadius="full"
                px={3}
                py={1}
              >
                Meta App Review Callback
              </Badge>

              <Heading size="lg">Meta Permission Grant Result</Heading>

              <Text fontSize="sm" color="gray.600">
                This page confirms that the reviewer returned from the
                Meta/Facebook permission grant flow.
              </Text>
            </Stack>

            <Alert status={status.status} borderRadius="16px">
              <AlertIcon />
              <Box>
                <Heading size="sm" mb={1}>
                  {status.title}
                </Heading>
                <AlertDescription>{status.description}</AlertDescription>
              </Box>
            </Alert>

            <Stack
              spacing={3}
              p={4}
              borderWidth="1px"
              borderColor="rgba(15,23,42,0.08)"
              borderRadius="16px"
              bg="rgba(248,250,252,0.92)"
            >
              <Text fontSize="sm">
                <strong>Authorization code received:</strong>{" "}
                {hasCode ? "Yes" : "No"}
              </Text>

              <Text fontSize="sm">
                <strong>State received:</strong> {hasState ? "Yes" : "No"}
              </Text>

              <Text fontSize="sm">
                <strong>State validation:</strong>{" "}
                {!storedState
                  ? "No stored state found in this browser session"
                  : stateMatches
                    ? "Passed"
                    : "Failed"}
              </Text>
            </Stack>

            <Stack direction={{ base: "column", sm: "row" }} spacing={3}>
              <Button
                colorScheme="blue"
                borderRadius="12px"
                onClick={() => navigate("/meta-review")}
              >
                Back to Meta Review Login
              </Button>

              <Button
                variant="outline"
                borderRadius="12px"
                onClick={() => navigate("/meta-review/dashboard")}
              >
                Continue to Review Dashboard
              </Button>
            </Stack>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default MetaReviewCallback;
